import { Check as CheckIcon, MoveToInbox as MoveIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { StyledFormControl, StyledMenuItem } from "@/components/ui";
import { useApiQuery } from "../../hooks/use-api-query";
import type { ApiConfig } from "../../types";
import type { TestCase } from "./types";

interface PromoteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (configId: string) => void;
  selectedTests: TestCase[];
  isLoading: boolean;
}

export const PromoteDialog: React.FC<PromoteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedTests,
  isLoading,
}) => {
  const [selectedConfigId, setSelectedConfigId] = useState<string>("");

  const { data, isLoading: isLoadingConfigs } = useApiQuery<ApiConfig>("/config");

  // Filter configs to only show production, staging, and development
  // const availableConfigs =
  //   data?.configs?.filter(
  //     (config) =>
  //       config.cfg_type === "production" ||
  //       config.cfg_type === "staging" ||
  //       config.cfg_type === "development",
  //   ) || [];

  const availableConfigs = useMemo(() => {
    return data?.configs ?? [];
  }, [data]);

  useEffect(() => {
    if (open) {
      setSelectedConfigId("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (selectedConfigId) {
      onConfirm(selectedConfigId);
    }
  };

  const handleClose = () => {
    setSelectedConfigId("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MoveIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
            Choose the config to promote to
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.875, pb: 0.875 }}>
        <Stack spacing={2.5}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Selected Tests ({selectedTests.length})
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              {selectedTests.map((test) => (
                <React.Fragment key={test.id}>
                  {test.name}
                  <br />
                </React.Fragment>
              ))}
            </Typography>
          </Paper>

          <StyledFormControl fullWidth size="small">
            <InputLabel>Target Configuration</InputLabel>
            <Select
              label="Target Configuration"
              value={selectedConfigId}
              onChange={(e) => setSelectedConfigId(e.target.value)}
              disabled={isLoadingConfigs}
            >
              {availableConfigs.map((config) => (
                <StyledMenuItem key={config.id} value={config.id}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="body2" fontWeight={500}>
                      {config.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {config.cfg_type} • {config.base_url}
                    </Typography>
                  </Stack>
                </StyledMenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 0.875, justifyContent: "flex-end" }}>
        <IconButton
          onClick={handleConfirm}
          disabled={!selectedConfigId || isLoading}
          sx={{
            width: 40,
            height: 40,
            color: "grey.600",
            "&:hover": {
              bgcolor: "grey.300",
            },
            "&:disabled": {
              bgcolor: "grey.200",
              color: "grey.400",
            },
          }}
        >
          <CheckIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};
